import { NavLinkItem, ButtonLogout } from 'motion-components';
import React, { FC, useState } from 'react';
import { Badge, Form, Nav, Navbar, NavDropdown } from 'react-bootstrap';
import { FaUser } from 'react-icons/fa';
import { generatePath, Link, useHistory } from 'react-router-dom';
import { getRouteByName } from './routes';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import { useUserPermissions } from 'shared-pages/hooks/user-permissions';
import { MainMenu } from './menu-items';
import { observer } from 'mobx-react-lite';
import './header.css';

const MyNavLinkItem = ({ text, routeName, disabled = false }) => {
  const { getUserCanView } = useUserPermissions();

  if (typeof routeName === 'object') {
    const rtName = routeName.routeName;
    const to = { ...routeName, pathname: getRouteByName(rtName) };
    const canView = getUserCanView(rtName);
    if (!canView || disabled) {
      return (
        <a className="pl-3 nav-link disabled" style={{ color: 'gray!important' }} href="/">
          {text}
        </a>
      );
    }
    return <NavLinkItem text={text} to={to} enable={canView} />;
  } else {
    const canView = getUserCanView(routeName);
    if (!canView || disabled) {
      return <a className="pl-3 nav-link disabled">{text}</a>;
    }
    return <NavLinkItem text={text} to={getRouteByName(routeName)} enable={canView} />;
  }
};

type Props = {
  onLogoutClick: () => void;
};
const Header: FC<Props> = observer(({ onLogoutClick }) => {
  /*-------------------- Body -----------------------------------------------*/
  const { appStore, authStore } = useRootStore();
  const history = useHistory();
  const [expanded, setExpanded] = useState(false);
  const { t, i18n } = useTranslation();

  /*-------------------- Handlers -------------------------------------------*/

  const handleLogoutClick = () => {
    setExpanded(false);
    onLogoutClick();
  };

  const handleToggle = (state: boolean) => {
    setExpanded(state);
  };

  const handleSelect = () => {
    setExpanded(false);
  };

  const handleUserClick = () => {
    console.log('handleUserClick', appStore.userLogin);
    history.push(generatePath(getRouteByName('User'), { id: appStore.userLogin.userId }));
  };

  /*-------------------- Render ---------------------------------------------*/
  return (
    <header>
      <Navbar
        expand="sm"
        bg="dark"
        variant="dark"
        fixed="top"
        onToggle={handleToggle}
        onSelect={handleSelect}
        expanded={expanded}
      >
        <Navbar.Brand as={Link} to={getRouteByName('Home')}>
          <img
            alt=""
            src={process.env.PUBLIC_URL + '/logo-32x32.png'}
            height="32"
            className="d-inline-block align-top mx-1"
          />
        </Navbar.Brand>
        {process.env.REACT_APP_DEMO === '1' && (
          <Badge className="ms-5 demo-badge-mobile" bg="warning">
            <h6 style={{ color: 'blue', marginTop: '5px' }}>
              <strong>&nbsp;&nbsp; DEMO VERSION &nbsp;&nbsp;</strong>
            </h6>
          </Badge>
        )}
        <Navbar.Toggle aria-controls="responsive-navbar-nav" />
        <Navbar.Collapse id="responsive-navbar-nav">
          <Nav className="mr-auto">
            {appStore.isLogIn && (
              <>
                <MainMenu />
              </>
            )}

            {/* Demo Badge */}
            {process.env.REACT_APP_DEMO === '1' && (
              <Badge className="ms-3 demo-badge" bg="warning" style={{ padding: '0.25rem 0.5rem' }}>
                <span style={{ color: 'blue', fontSize: '0.75rem', fontWeight: 'bold' }}>
                  DEMO VERSION
                </span>
              </Badge>
            )}

            {/* Dev Mode */}
            {appStore.devMode && (
              <Badge className="ms-5 p-0" bg="warning">
                <h6 style={{ color: 'blue', marginTop: '1px', marginBottom: '2px' }}>
                  <strong>&nbsp;&nbsp; DEV MODE &nbsp;&nbsp;</strong>
                </h6>
              </Badge>
            )}
          </Nav>

          {appStore.isLogIn && (
            <Nav className="ms-auto align-items-center">
              {/* Language + User */}
              <Navbar.Text className="btn btn-link pe-1 py-0" style={{ marginTop: '0' }}>
                <div
                  style={{
                    color: 'white',
                    opacity: i18n.language === 'en' ? 1 : 0.6,
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    i18n.changeLanguage('en');
                  }}
                >
                  {'EN'}
                  <img
                    alt=""
                    src={process.env.PUBLIC_URL + '/ukFlagEmoji.png'}
                    height={'16'}
                    className="d-inline-block align-middle mx-1"
                  />
                </div>
              </Navbar.Text>
              <Navbar.Text className="btn btn-link pe-2 py-0" style={{ marginTop: '0' }}>
                <div
                  style={{
                    color: 'white',
                    opacity: i18n.language !== 'en' ? 1 : 0.6,
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    i18n.changeLanguage('gr');
                  }}
                >
                  {'ΕΛ'}
                  <img
                    alt=""
                    src={process.env.PUBLIC_URL + '/greekFlagEmoji.png'}
                    height={'16'}
                    className="d-inline-block align-middle mx-1"
                  />
                </div>
              </Navbar.Text>
              {/* ΧΡΗΣΤΗΣ */}
              <Nav>
                <Navbar.Text
                  className="btn btn-link pe-2 py-0"
                  onClick={handleUserClick}
                  style={{ cursor: 'pointer', textDecoration: 'none' }}
                >
                  <div
                    className="d-flex flex-row justify-content-start align-items-center"
                    style={{ gap: '0.3rem' }}
                  >
                    <FaUser size="1.2rem" />
                    <div
                      className="d-flex flex-column"
                      style={{ maxWidth: 140, color: 'white', lineHeight: '1.1' }}
                    >
                      <span style={{ fontSize: '0.75rem' }} className="text-truncate text-start">
                        {appStore.user}
                      </span>
                      <span style={{ fontSize: '0.7rem' }} className="text-truncate text-start">
                        {appStore.userLogin.userFirstName + ' ' + appStore.userLogin.userLastName}
                      </span>
                    </div>
                  </div>
                </Navbar.Text>
              </Nav>
              <ButtonLogout onClick={handleLogoutClick} />
            </Nav>
          )}
        </Navbar.Collapse>
      </Navbar>
    </header>
  );
});
export default Header;
